import React from 'react'
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { setHistoryMap } from '../../../../store/historyState.ts'
import { LatLng } from '../../types.ts'
import { mapState, setMap, setZoom } from '../../../../store/mapState.ts'
import { createZoomControl } from './zoomControl.ts'
import { useSnapshot } from 'valtio'
import { controlState } from '../../../../store/controlState.ts'

const libraries = ['maps', 'geometry', 'places', 'drawing', 'marker', 'routes', 'geocoding']
export const POI_OFF = {
		featureType: "poi",
		stylers: [{ visibility: "off" }],
	}

interface Props {
	height: string
	center: LatLng
	zoom: number
	children?: React.ReactNode
	isHistory?: boolean
}
export function GoogleMapWrapper(props: Props) {
	const {height, center, zoom} = props
	const isMobile = useSnapshot(controlState).isOnMobileDevice

	const mapOptions = {
		// center and zoom are set programmatically in onLoad (from saved state or props).
		// Do NOT include them here — the GoogleMap component reapplies options on every
		// render, which would snap the map back to these values and block user zoom/pan.
		fullscreenControl: false,
		styles: [POI_OFF],
		disableDefaultUI: true,
		keyboardShortcuts: !isMobile,
	}

	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY,
		// @ts-ignore => no good cast for Libraries
		libraries,
	})

	const onLoad = React.useCallback(function callback(map: google.maps.Map) {
		const savedState = JSON.parse(localStorage.getItem('mapState') || '{}')
		const savedCenter = savedState.center
		// Reject saved state if center is near 0,0 (Gulf of Guinea — never a valid location)
		const isValidCenter = savedCenter
			&& (Math.abs(savedCenter.lat) > 1 || Math.abs(savedCenter.lng) > 1)
		if (isValidCenter && savedState.zoom) {
			map.setCenter(savedCenter)
			map.setZoom(savedState.zoom)
			setZoom(savedState.zoom)
		} else {
			localStorage.removeItem('mapState')
			map.setCenter(center)
			map.setZoom(zoom)
			setZoom(zoom)
		}
		
		map.addListener('click',  () => {
			mapState.markerInfo = null
		})
		
		map.addListener('center_changed', () => {
			if (window.location.pathname.includes('history')) return;
			const newCenter = map.getCenter()
			const currentZoom = map.getZoom()
			if (newCenter && currentZoom) {
				// Don't save 0,0 — it's never a valid pet location
				if (Math.abs(newCenter.lat()) < 1 && Math.abs(newCenter.lng()) < 1) return
				const timeoutId = setTimeout(() => {
					localStorage.setItem('mapState', JSON.stringify({
						zoom: currentZoom,
						center: { lat: newCenter.lat(), lng: newCenter.lng() }
					}))
				}, 500)
				;(map as any)._saveTimeout = timeoutId
			}
		})

		setMap(map)
		setHistoryMap(map)

		if (!isMobile) {
			const controls: any = map.controls[google.maps.ControlPosition.RIGHT_BOTTOM]
			if (controls && !controls.length) {
				createZoomControl(map)
			}
		}

		map.addListener('zoom_changed', () => {
			const currentZoom = map.getZoom()
			if (currentZoom != null) {
				if (!window.location.pathname.includes('history')) {
					setZoom(currentZoom)
				} else {
					mapState.historyZoom = currentZoom
				}
			}
		})
	}, [])

	const onUnmount = React.useCallback(function callback() {
		setMap(null)
		setHistoryMap(null)
	}, [])

	const containerStyle = {
		width: '100%',
		height,
	}

	return isLoaded ? (
		<GoogleMap
			mapContainerStyle={containerStyle}
			onLoad={onLoad}
			onUnmount={onUnmount}
			options={mapOptions}
		>
			{props.children}
		</GoogleMap>
	) : <></>
}

export default React.memo(GoogleMapWrapper)
